import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardContent, Typography, Box } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles } from "@material-ui/core/styles";
import { useRccTypography } from "../typography";
import { rccColors } from "../colors";
import { countOrACVFormatterMillion } from "../../util/customFunctions";
import { appService } from "../../App/app.service";

/**
 * YTD Bookings card
 *
 * This implementation closely follows the Figma layout:
 * - Outer white card with 12px radius and soft shadow
 * - Left container: YTD Bookings, main value, green pill, "1.29M vs Quota"
 * - Right container: STLY block with green background chip
 */
const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: "12px",
    backgroundColor: "#FFFFFF",
    boxShadow: "6px 6px 54px 0 rgba(0, 0, 0, 0.05)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
    },
  },
  contentRoot: {
    display: "flex",
    flex: "1 0 auto",
    alignItems: "stretch",
    padding: "16px 12px",
    width: "100%",
    boxSizing: "border-box",
    gap: "12px",
    // Override default Material-UI bottom padding
    "&:last-child": {
      paddingBottom: "16px",
    },
  },
  leftContainer: {
    display: "flex",
    flexDirection: "column",
    // 'space-between' pushes the Title to the top and the Data to the bottom
    justifyContent: "space-between",
    alignItems: "flex-start",
    // Removed fixed gap so space-between handles the vertical distribution
    flex: "1",
  },
  bottomBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "8px",
    alignSelf: "stretch",
  },
  rightContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    alignSelf: "stretch",
    // Ensure the right side fills the height too
    height: "100%",
  },
  metricBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "4px",
  },
  pill: {
    display: "flex",
    padding: "2px 8px",
    alignItems: "center",
    gap: "2px",
    borderRadius: "100px",
  },
  pillPositive: {
    backgroundColor: rccColors.green[50],
    color: rccColors.green[700],
  },
  pillNegative: {
    backgroundColor: rccColors.red[50],
    color: rccColors.red[700],
  },
  icon: {
    display: "flex",
    padding: "4px 2px",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    fontSize: "16px",
  },
}));

function YtdBookingsCard({ title, redirectToDashboard, onDrilldown }) {
  const classes = useStyles();
  const typography = useRccTypography();
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);
  const user = useSelector((state) => state.app.user || {});

  // Fetch data directly from Redux
  const homepageData = useSelector((state) => state.app.homepageData || "");
  const differentQtrData = homepageData?.differentQtrData || {};

  // Check if data is loading
  const isLoading = homepageData === "";

  // Fetch data if not available
  useEffect(() => {
    if (homepageData === "" && !fetchingRef.current) {
      fetchingRef.current = true;
      dispatch({ type: "get_homepage_data_request" });
      appService.homepageData().then(
        (resp) => {
          dispatch({
            type: "get_homepage_data_success",
            homepageData: {
              data: resp.message.recordsets,
              columns: resp.message.columns,
              quartersToUse: resp.message.quartersToUse,
              differentQtrData: resp.message.differentQtrData,
            },
          });
          fetchingRef.current = false;
        },
        (err) => {
          dispatch({ type: "get_homepage_data_failure", error: err });
          fetchingRef.current = false;
        }
      );
    }
  }, [homepageData, dispatch]);

  // Show loading state with skeleton
  if (isLoading) {
    return (
      <Card className={classes.card} style={{ height: "100%" }}>
        <CardContent className={classes.contentRoot} style={{ height: "100%" }}>
          <Skeleton
            variant="rect"
            width="100%"
            height="100%"
            animation="wave"
            style={{ borderRadius: "8px" }}
          />
        </CardContent>
      </Card>
    );
  }

  const ytdACV = differentQtrData.ytdACV || 0;
  const ytdQuota = differentQtrData.ytdQuota || 0;
  const ytdPerc = differentQtrData.ytdPerc || 0;
  const priorYTDQuartersPerc = differentQtrData.priorYTDQuartersPerc || 0;

  // Calculate values
  const formattedACV = countOrACVFormatterMillion(ytdACV, true);
  const acvDisplay = formattedACV.replace(/^\$/, "");
  const formattedQuota = countOrACVFormatterMillion(ytdQuota, true);
  const quotaDisplay = formattedQuota.replace(/^\$/, "");
  const attainmentPercent = (ytdPerc * 100).toFixed(1);

  // Calculate percentage change
  const stlyPercChange = (ytdPerc - priorYTDQuartersPerc) * 100;
  const stlyFormatted = `${
    stlyPercChange >= 0 ? "+" : ""
  }${stlyPercChange.toFixed(1)}%`;
  const stlyIsPositive = stlyPercChange >= 0;

  return (
    <Card className={classes.card}>
      <CardContent className={classes.contentRoot}>
        {/* Left container – YTD Bookings */}
        <Box className={classes.leftContainer}>
          {/* Top box: YTD Bookings label */}
          <Box>
            <Typography
              className={typography.textMediumSemibold}
              style={{ color: rccColors.neutral[800] }}
            >
              {title || "YTD Bookings"}
            </Typography>
          </Box>

          {/* Bottom box: value + green pill + vs Quota row */}
          <Box className={classes.bottomBox}>
            <Typography
              className={typography.displayMediumSemibold}
              style={{
                color: rccColors.neutral[900],
                cursor: onDrilldown ? "pointer" : "default",
              }}
              onClick={onDrilldown}
            >
              {user?.Currency}
              {acvDisplay}
            </Typography>

            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <span className={`${classes.pill} ${classes.pillPositive}`}>
                <Typography
                  component="span"
                  className={typography.textMediumSemibold}
                >
                  {attainmentPercent}%
                </Typography>
              </span>
              <Typography
                component="span"
                className={typography.textExtraSmallRegular}
                style={{ color: rccColors.neutral[500] }}
              >
                vs Quota of
              </Typography>
              <Typography
                component="span"
                className={typography.textMediumSemibold}
                style={{ color: rccColors.neutral[500] }}
              >
                {user?.Currency}
                {quotaDisplay}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right container – STLY */}
        <Box className={classes.rightContainer}>
          {/* STLY */}
          <Box className={classes.metricBox}>
            <Typography
              className={`${typography.textExtraSmallRegular}`}
              style={{ color: rccColors.neutral[500] }}
            >
              STLY:
            </Typography>
            <span
              className={`${classes.pill} ${
                stlyIsPositive ? classes.pillPositive : classes.pillNegative
              }`}
            >
              <i className={`material-icons ${classes.icon}`}>{`trending_${
                stlyIsPositive ? "up" : "down"
              }`}</i>
              <Typography
                component="span"
                className={typography.textSmallSemibold}
              >
                {stlyFormatted}
              </Typography>
            </span>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default YtdBookingsCard;
